import React, { useState, useRef, useEffect } from 'react';
import { Share2, Image, Video, X, Download, Play, Pause } from 'lucide-react';
import { Message, ReplayAction } from '../types';

interface ShareButtonProps {
  messages: Message[];
  replayActions: ReplayAction[];
  isDarkMode: boolean;
  gameOutcome?: 'win' | 'lose' | 'alternative_win' | null;
  outcomeReason?: string;
}

export const ShareButton: React.FC<ShareButtonProps> = ({ 
  messages, 
  replayActions,
  isDarkMode, 
  gameOutcome,
  outcomeReason 
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState<'image' | 'video'>('image');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [replayIndex, setReplayIndex] = useState(0);
  const [currentUserInput, setCurrentUserInput] = useState('');
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const replayIntervalRef = useRef<number | null>(null);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (replayIntervalRef.current) {
        clearInterval(replayIntervalRef.current);
      }
    };
  }, []);

  const generateImage = async () => {
    setIsGenerating(true);
    try {
      // Dynamic import html2canvas
      const html2canvas = (await import('html2canvas')).default;
      
      if (previewRef.current) {
        const canvas = await html2canvas(previewRef.current, {
          backgroundColor: isDarkMode ? '#1f2937' : '#f9fafb',
          scale: 2,
          useCORS: true,
        });
        const dataUrl = canvas.toDataURL('image/png');
        setImageDataUrl(dataUrl);
      }
    } catch (err) {
      console.error('Failed to generate image:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShareImage = async () => {
    setPreviewMode('image');
    setShowMenu(false);
    setShowPreview(true);
    setImageDataUrl(null);
    // Generate image after modal opens
    setTimeout(generateImage, 100);
  };

  const handleShareVideo = () => {
    setPreviewMode('video');
    setShowMenu(false);
    setShowPreview(true);
    setReplayIndex(0);
    setCurrentUserInput('');
    setIsPlaying(false);
  };

  const startReplay = () => {
    setIsPlaying(true);
    setReplayIndex(0);
    setCurrentUserInput('');
    
    replayIntervalRef.current = window.setInterval(() => {
      setReplayIndex(prev => {
        if (prev >= replayActions.length - 1) {
          if (replayIntervalRef.current) {
            clearInterval(replayIntervalRef.current);
          }
          setIsPlaying(false);
          return prev;
        }
        
        // Update current user input for word actions
        const nextAction = replayActions[prev + 1];
        if (nextAction.type === 'word') {
          setCurrentUserInput(prevInput => {
            const spacing = prevInput.length > 0 && !prevInput.endsWith(" ") ? " " : "";
            return prevInput + spacing + (nextAction.word || '');
          });
        } else if (nextAction.type === 'message' && nextAction.message?.sender === 'user') {
          // Clear input when user message is sent
          setCurrentUserInput('');
        }
        
        return prev + 1;
      });
    }, 600); // Slower pace for word-by-word
  };

  const pauseReplay = () => {
    if (replayIntervalRef.current) {
      clearInterval(replayIntervalRef.current);
    }
    setIsPlaying(false);
  };

  // Build visible messages and current input based on replay progress
  const getReplayState = () => {
    const visibleMessages: Message[] = [];
    let buildingInput = '';
    
    for (let i = 0; i <= replayIndex && i < replayActions.length; i++) {
      const action = replayActions[i];
      if (action.type === 'message') {
        if (action.message) {
          visibleMessages.push(action.message);
        }
      } else if (action.type === 'word') {
        const spacing = buildingInput.length > 0 && !buildingInput.endsWith(" ") ? " " : "";
        buildingInput += spacing + (action.word || '');
      }
    }
    
    // Check if the next action clears the input (user message sent)
    if (replayIndex + 1 < replayActions.length) {
      const nextAction = replayActions[replayIndex + 1];
      if (nextAction.type === 'message' && nextAction.message?.sender === 'user') {
        buildingInput = '';
      }
    }
    
    return { visibleMessages, buildingInput };
  };

  const { visibleMessages, buildingInput } = previewMode === 'video' 
    ? getReplayState() 
    : { visibleMessages: messages, buildingInput: '' };

  const downloadImage = () => {
    if (imageDataUrl) {
      const link = document.createElement('a');
      link.download = `autocorrect-adventure-${Date.now()}.png`;
      link.href = imageDataUrl;
      link.click();
    }
  };

  const shareToClipboard = async () => {
    if (imageDataUrl) {
      try {
        const response = await fetch(imageDataUrl);
        const blob = await response.blob();
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        alert('Image copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy:', err);
        // Fallback: download instead
        downloadImage();
      }
    }
  };

  if (messages.length === 0) return null;

  return (
    <>
      {/* Share Button */}
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className={`p-2 rounded-full transition-colors ${
            isDarkMode 
              ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
              : 'bg-white text-gray-700 hover:bg-gray-100'
          } shadow-lg`}
          aria-label="Share adventure"
        >
          <Share2 size={20} />
        </button>

        {/* Dropdown Menu */}
        {showMenu && (
          <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl z-20 overflow-hidden ${
            isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <button
              onClick={handleShareImage}
              className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <Image size={18} />
              <span>Share as Image</span>
            </button>
            <button
              onClick={handleShareVideo}
              className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <Video size={18} />
              <span>Video Replay</span>
            </button>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className={`w-full max-w-md max-h-[90vh] rounded-2xl overflow-hidden flex flex-col ${
            isDarkMode ? 'bg-gray-900' : 'bg-white'
          }`}>
            {/* Modal Header */}
            <div className={`flex items-center justify-between px-4 py-3 border-b ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                {previewMode === 'image' ? 'Share as Image' : 'Video Replay'}
              </h3>
              <button
                onClick={() => {
                  setShowPreview(false);
                  pauseReplay();
                }}
                className={`p-1 rounded-full ${
                  isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}
              >
                <X size={20} />
              </button>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-auto">
              <div 
                ref={previewRef}
                className={`p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}
              >
                {/* Header */}
                <div className={`text-center mb-4 pb-3 border-b ${
                  isDarkMode ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <h4 className={`font-bold text-lg ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    Autocorrect Adventure
                  </h4>
                  {gameOutcome && (
                    <p className={`text-sm mt-1 ${
                      gameOutcome === 'win' || gameOutcome === 'alternative_win'
                        ? 'text-green-500'
                        : 'text-red-500'
                    }`}>
                      {gameOutcome === 'win' ? '🏆 Victory!' : 
                       gameOutcome === 'alternative_win' ? '✨ Unexpected Victory!' : 
                       '💀 Game Over'}
                    </p>
                  )}
                </div>

                {/* Messages */}
                <div className="space-y-3">
                  {visibleMessages.map((msg, idx) => (
                    <div
                      key={msg.id}
                      className={`flex w-full ${
                        msg.sender === 'user' ? 'justify-end' : 'justify-start'
                      } ${previewMode === 'video' && idx === visibleMessages.length - 1 ? 'animate-fade-in' : ''}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                          msg.sender === 'user'
                            ? 'bg-blue-500 text-white rounded-br-none'
                            : isDarkMode
                            ? 'bg-gray-700 text-gray-200 rounded-bl-none'
                            : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
                        }`}
                      >
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  
                  {/* Show building input during video replay */}
                  {previewMode === 'video' && buildingInput && (
                    <div className="flex justify-end animate-fade-in">
                      <div className="max-w-[80%] rounded-2xl px-3 py-2 text-sm bg-blue-400 text-white rounded-br-none opacity-70">
                        {buildingInput}
                        <span className="inline-block w-1 h-4 bg-white ml-1 animate-pulse"></span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Outcome */}
                {outcomeReason && previewMode === 'image' && (
                  <div className={`mt-4 pt-3 border-t text-center text-sm ${
                    isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'
                  }`}>
                    {outcomeReason}
                  </div>
                )}

                {/* Watermark */}
                <div className={`mt-4 text-center text-xs ${
                  isDarkMode ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  autocorrect-adventure.vercel.app
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className={`flex gap-2 p-4 border-t ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              {previewMode === 'image' ? (
                <>
                  <button
                    onClick={shareToClipboard}
                    disabled={isGenerating || !imageDataUrl}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                      isDarkMode 
                        ? 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500' 
                        : 'bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400'
                    }`}
                  >
                    <Share2 size={16} />
                    {isGenerating ? 'Generating...' : 'Copy to Clipboard'}
                  </button>
                  <button
                    onClick={downloadImage}
                    disabled={isGenerating || !imageDataUrl}
                    className={`py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300'
                    }`}
                  >
                    <Download size={16} />
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={isPlaying ? pauseReplay : startReplay}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                      isDarkMode 
                        ? 'bg-blue-600 text-white hover:bg-blue-700' 
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                    {isPlaying ? 'Pause' : replayIndex > 0 ? 'Resume' : 'Play Replay'}
                  </button>
                  <button
                    onClick={() => {
                      setReplayIndex(0);
                      setCurrentUserInput('');
                      pauseReplay();
                    }}
                    disabled={replayIndex === 0}
                    className={`py-2 px-4 rounded-lg font-medium transition-colors ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300'
                    }`}
                  >
                    Reset
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close menu */}
      {showMenu && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setShowMenu(false)}
        />
      )}
    </>
  );
};
